import React, { useMemo } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Col,
  ProgressBar,
  Row,
  Spinner,
} from 'react-bootstrap';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import { useFetchAdsurePodTestsQuery } from 'services/api/creativePreview';
import { useUpdateAdsurePodStatusMutation } from 'services/api/creativeServer';
import { PodInfo } from './types';
import { formatTimestamp, getHealthSummary } from './utils';
import PodTestsTable from './PodTestsTable';

export default function PodMonitoringCard({
  podName,
  podInfo,
  podSourcesParam,
}: {
  podName: string;
  podInfo: PodInfo;
  podSourcesParam: string;
}) {
  const { data, isLoading, isFetching, error } = useFetchAdsurePodTestsQuery(
    {
      pod: podName,
      sources: podSourcesParam,
    },
    {
      pollingInterval: 15000,
      refetchOnMountOrArgChange: true,
    },
  );
  const [updatePodStatus, { isLoading: isUpdatingStatus }] =
    useUpdateAdsurePodStatusMutation();

  const tests = useMemo(() => data?.data || [], [data]);
  const health = useMemo(() => getHealthSummary(tests), [tests]);
  const metadata = podInfo.metadata || {};
  const isAvailable = Boolean(podInfo.available && podInfo.isPodAvailable);

  const handlePodStatusUpdate = async (
    event: React.MouseEvent<HTMLButtonElement>,
    status: boolean,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await updatePodStatus({ pod: podName, status }).unwrap();
      toast.success(`${status ? 'Started' : 'Stopped'} ${podName}.`);
    } catch (updateError) {
      toast.error(get(updateError, 'data.error', 'Failed to update pod status'));
    }
  };

  return (
    <Accordion.Item eventKey={podName}>
      <div className="adsure-pod-accordion-header">
        <Accordion.Header>
          <div className="w-100 pe-3">
            <Row className="align-items-center g-3 w-100">
              <Col lg={6}>
                <div className="fw-bold text-dark">{podName}</div>
                <div className="text-muted small mt-1">
                  {metadata.regionCode || 'NA'} | WebOS {metadata.osv || 'NA'} |{' '}
                  {metadata.server || 'NA'}
                </div>
              </Col>
              <Col lg={4}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="fw-semibold">Health</span>
                  <Badge bg={health.variant}>{health.label}</Badge>
                </div>
                <ProgressBar
                  now={health.percentage}
                  variant={health.variant}
                  style={{ height: 8, borderRadius: 999 }}
                />
              </Col>
              <Col lg={2}>
                <div className="fw-semibold mb-1">Pod Status</div>
                <Badge bg={isAvailable ? 'success' : 'secondary'}>
                  {isAvailable ? 'Running' : 'Stopped'}
                </Badge>
              </Col>
            </Row>
          </div>
        </Accordion.Header>
        <div className="adsure-pod-status-actions">
          <Button
            size="sm"
            variant="success"
            onClick={event => handlePodStatusUpdate(event, true)}
            disabled={isUpdatingStatus}
          >
            Start
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={event => handlePodStatusUpdate(event, false)}
            disabled={isUpdatingStatus}
          >
            Stop
          </Button>
        </div>
      </div>
      <Accordion.Body>
        <div className="adsure-meta-grid">
          <div className="adsure-meta-field adsure-meta-field-countries">
            <div className="adsure-section-label">Countries</div>
            <div className="adsure-meta-value">
              {(metadata.supportedCountries || []).join(', ') || 'NA'}
            </div>
          </div>
          <div className="adsure-meta-field adsure-meta-field-adslots">
            <div className="adsure-section-label">Ad Slots</div>
            <div className="adsure-meta-value">
              {(metadata.adSlots || []).slice(0, 3).join(', ') || 'NA'}
            </div>
          </div>
          <div className="adsure-meta-field adsure-meta-field-updated">
            <div className="adsure-section-label">Last Updated</div>
            <div className="adsure-meta-value">
              {formatTimestamp(podInfo.lastUpdated)}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="adsure-loading-state">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading test history...
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            Failed to load recent tests for {podName}.
          </Alert>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="fw-semibold">Last 10 test cases</div>
              {isFetching ? (
                <div className="text-muted small">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Refreshing
                </div>
              ) : null}
            </div>
            <PodTestsTable podName={podName} tests={tests} />
          </>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
}
